import { Component, Injector, OnInit } from '@angular/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AdendoPecasService } from 'src/app/services/adendo/adendo-pecas.service';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
    selector: 'app-form-comparacao-ipi',
    templateUrl: './formcomparacaoipi.component.html',
    styleUrls: ['../../../generic/formgenerico/formgenerico.component.css'],
})
export class FormComparacaoIpiComponent implements OnInit {

    arquivosSelecionados: File[] = [];
    comparacoesIpi: any[] = [];
    carregando: boolean = false;
    erroUpload: string = '';
    carregandoAtualizacao: boolean = false;

    // Filtros
    filtroIpiDiferente: boolean = false;

    // Controle de atualização (chave = NCM, pois esse fluxo não depende de P/N)
    itensSelecionadosParaAtualizar: Set<string> = new Set();

    // Controle de expansão da seção de upload
    secaoUploadExpandida: boolean = true;

    // Controle do checkbox master
    checkboxMasterSelecionado: boolean = false;

    // true = precisa fazer upload de XML (entrada direta pelo menu)
    // false = já veio com a comparação pronta (fluxo sequencial após a atualização de NCM)
    modoDireto: boolean = true;

    constructor(
        private injectorObj: Injector,
        private apiAdendoPecas: AdendoPecasService,
        public refDialog: DynamicDialogRef,
        public configDialog: DynamicDialogConfig,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private dialogService: DialogService
    ) { }

    ngOnInit(): void {
        const comparacoesPreCarregadas = this.configDialog?.data?.comparacoesIpi;

        if (comparacoesPreCarregadas && comparacoesPreCarregadas.length > 0) {
            this.modoDireto = false;
            this.secaoUploadExpandida = false;
            this.comparacoesIpi = comparacoesPreCarregadas.map((item: any) => ({
                ...item,
                selecionado: false
            }));
        }
    }

    onFileSelect(event: any) {
        this.arquivosSelecionados = [];
        this.erroUpload = '';

        if (event.files && event.files.length > 0) {
            for (let file of event.files) {
                if (file.name.toLowerCase().endsWith('.xml')) {
                    this.arquivosSelecionados.push(file);
                } else {
                    this.erroUpload = 'Apenas arquivos XML são permitidos!';
                }
            }
        }
    }

    onFileChange(event: any) {
        this.erroUpload = '';
        const files = event.target.files;

        if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                if (file.name.toLowerCase().endsWith('.xml')) {
                    if (!this.arquivosSelecionados.find(f => f.name === file.name)) {
                        this.arquivosSelecionados.push(file);
                    }
                } else {
                    this.erroUpload = 'Apenas arquivos XML são permitidos!';
                }
            }
        }

        event.target.value = '';
    }

    removerArquivo(index: number) {
        this.arquivosSelecionados.splice(index, 1);
    }

    limparArquivos() {
        this.arquivosSelecionados = [];
        this.comparacoesIpi = [];
        this.erroUpload = '';
    }

    toggleSecaoUpload() {
        this.secaoUploadExpandida = !this.secaoUploadExpandida;
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }

    // Processa os XMLs selecionados: reaproveita o parser de NCM (que já extrai
    // o ALIQUOTAIPI de cada item) só para ler os arquivos, deduplica por NCM e
    // manda comparar contra a FISCAL_NCM.
    processarXMLs() {
        if (this.arquivosSelecionados.length === 0) {
            this.erroUpload = 'Selecione pelo menos um arquivo XML!';
            return;
        }

        this.carregando = true;
        this.erroUpload = '';
        this.comparacoesIpi = [];

        const formData = new FormData();
        this.arquivosSelecionados.forEach(file => {
            formData.append('files', file);
        });

        this.apiAdendoPecas.compararNcmXml(formData).subscribe({
            next: (itensXml: any[]) => {
                const itensParaComparar = this.deduplicarPorNcm(itensXml || []);

                if (itensParaComparar.length === 0) {
                    this.carregando = false;
                    this.erroUpload = 'Nenhum NCM com alíquota de IPI encontrado nos XMLs.';
                    return;
                }

                this.compararIpi(itensParaComparar);
            },
            error: (error) => {
                console.error('Erro ao processar XMLs:', error);
                this.erroUpload = 'Erro ao processar XMLs: ' + (error.error?.message || error.message);
                this.carregando = false;
            }
        });
    }

    private deduplicarPorNcm(itensXml: any[]): any[] {
        const mapaUnico = new Map<string, any>();

        itensXml.forEach((item: any) => {
            const ncm = item.ncmXml || item.ncm;
            const aliquota = item.aliquotaIpiXml;
            const pn = item.pn;

            if (!ncm || aliquota === null || aliquota === undefined || aliquota === '') {
                return;
            }

            if (!mapaUnico.has(ncm)) {
                mapaUnico.set(ncm, {
                    ncm: ncm,
                    aliquotaIpiXml: aliquota,
                    descricao: item.descricao,
                    arquivo: item.arquivo,
                    pns: []
                });
            }

            // Acumula todos os P/Ns do XML que pertencem a esse NCM (sem duplicar)
            if (pn && !mapaUnico.get(ncm).pns.includes(pn)) {
                mapaUnico.get(ncm).pns.push(pn);
            }
        });

        return Array.from(mapaUnico.values());
    }

    private compararIpi(itensParaComparar: any[]) {
        this.carregando = true;

        this.apiAdendoPecas.compararAliquotaIpi(itensParaComparar).subscribe({
            next: (resultado: any[]) => {
                this.comparacoesIpi = resultado.map(item => ({
                    ...item,
                    selecionado: false
                }));

                this.carregando = false;
                this.secaoUploadExpandida = false;

                this.itensSelecionadosParaAtualizar.clear();
                this.checkboxMasterSelecionado = false;

                if (this.comparacoesIpi.length === 0) {
                    this.erroUpload = 'Nenhum item encontrado no processamento.';
                }
            },
            error: (error) => {
                console.error('Erro ao comparar alíquotas de IPI:', error);
                this.erroUpload = 'Erro ao comparar alíquotas de IPI: ' + (error.error?.message || error.message);
                this.carregando = false;
            }
        });
    }

    toggleFiltroIpiDiferente() {
        this.filtroIpiDiferente = !this.filtroIpiDiferente;
        setTimeout(() => this.atualizarCheckboxMaster(), 0);
    }

    get comparacoesExibidas(): any[] {
        let resultado = this.comparacoesIpi;

        if (this.filtroIpiDiferente) {
            resultado = resultado.filter(c => c.ipiDiferente === true);
        }

        return resultado;
    }

    get totalItens(): number {
        return this.comparacoesIpi.length;
    }

    get totalDiferentes(): number {
        return this.comparacoesIpi.filter(c => c.ipiDiferente === true && c.ncmEncontrado !== false).length;
    }

    get totalIguais(): number {
        return this.comparacoesIpi.filter(c => c.ipiDiferente === false && c.ncmEncontrado !== false).length;
    }

    get totalNaoCadastrados(): number {
        return this.comparacoesIpi.filter(c => c.ncmEncontrado === false).length;
    }

    get totalSelecionados(): number {
        return this.itensSelecionadosParaAtualizar.size;
    }

    // Todos os NCMs com FISCAL_NCM cadastrada (iguais + diferentes) -- os únicos
    // que realmente podem ser atualizados/reaplicados.
    get totalAtualizaveis(): number {
        return this.comparacoesIpi.filter(c => c.ncmEncontrado !== false).length;
    }

    fechar() {
        this.refDialog.close();
    }

    // Atualizar IPI individual
    atualizarIpiIndividual(item: any) {
        this.confirmationService.confirm({
            message: `Deseja realmente atualizar a alíquota de IPI do NCM "${item.ncm}" para ${item.aliquotaIpiNovo}?`,
            header: 'Confirmar Atualização',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.carregandoAtualizacao = true;

                this.apiAdendoPecas.atualizarAliquotaIpi(item.ncm, item.aliquotaIpiNovo, item.pns).subscribe({
                    next: (response: any) => {
                        const itensAtualizados = response?.itensAtualizados;
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: `Alíquota de IPI do NCM "${item.ncm}" atualizada com sucesso!` +
                                (itensAtualizados ? ` NCM reaplicado em ${itensAtualizados} peça(s).` : '')
                        });

                        item.aliquotaIpiAtualEnt = item.aliquotaIpiNovo;
                        item.aliquotaIpiAtualSai = item.aliquotaIpiNovo;
                        item.ipiDiferente = false;

                        this.carregandoAtualizacao = false;
                    },
                    error: (error) => {
                        console.error('Erro ao atualizar alíquota de IPI:', error);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: 'Erro ao atualizar alíquota de IPI: ' + (error.error?.message || error.message)
                        });
                        this.carregandoAtualizacao = false;
                    }
                });
            }
        });
    }

    // Atualizar todos os IPIs diferentes
    atualizarTodosDiferentes() {
        const itensDiferentes = this.comparacoesIpi.filter(c =>
            c.ipiDiferente === true &&
            c.ncmEncontrado !== false
        );

        if (itensDiferentes.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Não há NCMs com alíquota de IPI diferente para atualizar.'
            });
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente atualizar a alíquota de IPI de ${itensDiferentes.length} NCM(s) diferentes?`,
            header: 'Confirmar Atualização em Lote',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.executarAtualizacaoEmLote(itensDiferentes);
            }
        });
    }

    // Atualizar TODOS os NCMs cadastrados na FISCAL_NCM (iguais + diferentes).
    // Para os "iguais" isso só resalva a mesma alíquota e força a reaplicação
    // do NCM nos P/Ns daquele NCM -- útil quando o item ainda está com o NCM
    // desatualizado mesmo com a alíquota de IPI já correta.
    atualizarTodos() {
        const itensAtualizaveis = this.comparacoesIpi.filter(c => c.ncmEncontrado !== false);

        if (itensAtualizaveis.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Não há NCMs cadastrados na FISCAL_NCM para atualizar.'
            });
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente atualizar/reaplicar os ${itensAtualizaveis.length} NCM(s) (alíquotas iguais e diferentes)? ` +
                `Isso vai reaplicar o NCM em todas as peças (P/Ns) desses NCMs no XML.`,
            header: 'Confirmar Atualização de Todos',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.executarAtualizacaoEmLote(itensAtualizaveis);
            }
        });
    }

    // Atualizar apenas os selecionados
    atualizarSelecionados() {
        const itensSelecionados = this.comparacoesIpi.filter(c =>
            this.itensSelecionadosParaAtualizar.has(c.ncm)
        );

        if (itensSelecionados.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Nenhum NCM selecionado para atualizar.'
            });
            return;
        }

        this.confirmationService.confirm({
            message: `Deseja realmente atualizar a alíquota de IPI de ${itensSelecionados.length} NCM(s) selecionado(s)?`,
            header: 'Confirmar Atualização',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.executarAtualizacaoEmLote(itensSelecionados);
            }
        });
    }

    // Executa a atualização em lote
    executarAtualizacaoEmLote(itens: any[]) {
        this.carregandoAtualizacao = true;

        const atualizacoes = itens.map(item => ({
            ncmId: item.ncm,
            aliquotaIpi: item.aliquotaIpiNovo,
            pns: item.pns
        }));

        this.apiAdendoPecas.atualizarAliquotaIpiEmLote(atualizacoes).subscribe({
            next: (response: any) => {
                itens.forEach(item => {
                    item.aliquotaIpiAtualEnt = item.aliquotaIpiNovo;
                    item.aliquotaIpiAtualSai = item.aliquotaIpiNovo;
                    item.ipiDiferente = false;
                    item.selecionado = false;
                });

                this.itensSelecionadosParaAtualizar.clear();
                this.checkboxMasterSelecionado = false;
                this.carregandoAtualizacao = false;

                const itensAtualizados = response?.itensAtualizados;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Atualização Concluída',
                    detail: `${response.sucesso} alíquota(s) de IPI atualizada(s) com sucesso! ${response.erro > 0 ? response.erro + ' erro(s).' : ''}` +
                        (itensAtualizados ? ` NCM reaplicado em ${itensAtualizados} peça(s) no total.` : '')
                });

                if (response.erros && response.erros.length > 0) {
                    console.error('Erros na atualização de IPI:', response.erros);
                }
            },
            error: (error) => {
                console.error('Erro ao atualizar alíquotas de IPI:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao atualizar alíquotas de IPI: ' + (error.error?.message || error.message)
                });
                this.carregandoAtualizacao = false;
            }
        });
    }

    // Toggle seleção de item
    toggleSelecao(item: any) {
        if (this.itensSelecionadosParaAtualizar.has(item.ncm)) {
            this.itensSelecionadosParaAtualizar.delete(item.ncm);
            item.selecionado = false;
        } else {
            this.itensSelecionadosParaAtualizar.add(item.ncm);
            item.selecionado = true;
        }

        this.atualizarCheckboxMaster();
    }

    // Selecionar/Desselecionar todos diferentes
    toggleTodosDiferentes() {
        const itensDiferentes = this.comparacoesExibidas.filter(c =>
            c.ipiDiferente === true &&
            c.ncmEncontrado !== false
        );

        const todosSelecionados = itensDiferentes.every(item =>
            this.itensSelecionadosParaAtualizar.has(item.ncm)
        );

        if (todosSelecionados) {
            itensDiferentes.forEach(item => {
                this.itensSelecionadosParaAtualizar.delete(item.ncm);
                item.selecionado = false;
            });
            this.checkboxMasterSelecionado = false;
        } else {
            itensDiferentes.forEach(item => {
                this.itensSelecionadosParaAtualizar.add(item.ncm);
                item.selecionado = true;
            });
            this.checkboxMasterSelecionado = true;
        }
    }

    atualizarCheckboxMaster() {
        const itensDiferentes = this.comparacoesExibidas.filter(c =>
            c.ipiDiferente === true &&
            c.ncmEncontrado !== false
        );

        if (itensDiferentes.length === 0) {
            this.checkboxMasterSelecionado = false;
            return;
        }

        const todosSelecionados = itensDiferentes.every(item =>
            this.itensSelecionadosParaAtualizar.has(item.ncm)
        );

        this.checkboxMasterSelecionado = todosSelecionados;
    }
}
